import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BookOpen, Tag, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatDate(str) {
  if (!str) return '';
  try {
    return new Date(str).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return str;
  }
}

export default function BlogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get('page') || '1', 10);
  const tag = searchParams.get('tag') || '';

  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    const params = new URLSearchParams({ page, limit: 12 });
    if (tag) params.set('tag', tag);
    fetch(`${API}/blog/posts?${params}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) { setPosts(d.posts); setTotal(d.total); setPages(d.pages); }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [page, tag]);

  const goPage = (p) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', p);
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#fafaf9]">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 text-stone-600 hover:text-[#1e3a8a] transition-colors">
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
              <span className="hidden sm:inline text-sm">Back</span>
            </Link>
            <Link to="/" className="font-serif text-xl font-bold text-[#1e3a8a]">SermonSift</Link>
            <Link to="/dashboard">
              <Button variant="ghost" className="text-sm text-[#1e3a8a]">Dashboard</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-[#1e3a8a] py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">
            <BookOpen className="w-3.5 h-3.5" /> SermonSift Blog
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            Insights for Ministry Leaders
          </h1>
          <p className="text-blue-200 text-lg max-w-2xl mx-auto">
            Practical articles on sermon study, preaching, and using technology to deepen your ministry.
          </p>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-14">

        {/* Active tag filter */}
        {tag && (
          <div className="flex items-center gap-2 mb-8">
            <span className="text-sm text-stone-500">Filtered by:</span>
            <span className="inline-flex items-center gap-1.5 bg-[#1e3a8a]/10 text-[#1e3a8a] text-sm font-medium px-3 py-1 rounded-full">
              <Tag className="w-3 h-3" /> {tag}
            </span>
            <button
              onClick={() => { const p = new URLSearchParams(); setSearchParams(p); }}
              className="text-xs text-stone-400 hover:text-stone-600 underline ml-1"
            >
              Clear
            </button>
          </div>
        )}

        {/* Posts grid */}
        {isLoading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="card p-0 overflow-hidden animate-pulse">
                <div className="bg-stone-200 h-48 w-full" />
                <div className="p-5 space-y-3">
                  <div className="h-3 bg-stone-200 rounded w-1/3" />
                  <div className="h-5 bg-stone-200 rounded w-3/4" />
                  <div className="h-3 bg-stone-200 rounded w-full" />
                  <div className="h-3 bg-stone-200 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-24">
            <BookOpen className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h2 className="font-serif text-xl font-bold text-stone-500 mb-2">No posts yet</h2>
            <p className="text-stone-400 text-sm">Check back soon — articles are on their way.</p>
          </div>
        ) : (
          <>
            {/* Featured first post */}
            {page === 1 && !tag && posts[0] && (
              <Link to={`/blog/${posts[0].slug}`} className="group block mb-10">
                <div className="card p-0 overflow-hidden lg:flex">
                  {posts[0].featured_image ? (
                    <div className="lg:w-1/2 h-64 lg:h-auto overflow-hidden flex-shrink-0">
                      <img
                        src={posts[0].featured_image}
                        alt={posts[0].title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="lg:w-1/2 h-64 lg:h-auto bg-gradient-to-br from-[#1e3a8a] to-[#2d52b0] flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-16 h-16 text-white/30" />
                    </div>
                  )}
                  <div className="p-8 lg:p-10 flex flex-col justify-center">
                    {posts[0].tags?.length > 0 && (
                      <span className="inline-flex items-center gap-1 text-[#1e3a8a] text-xs font-semibold uppercase tracking-wider mb-3">
                        <Tag className="w-3 h-3" /> {posts[0].tags[0]}
                      </span>
                    )}
                    <h2 className="font-serif text-2xl lg:text-3xl font-bold text-[#1e3a8a] mb-3 leading-snug group-hover:text-[#2d52b0] transition-colors">
                      {posts[0].title}
                    </h2>
                    {posts[0].excerpt && (
                      <p className="text-stone-600 leading-relaxed mb-5 line-clamp-3">{posts[0].excerpt}</p>
                    )}
                    <div className="flex items-center gap-2 text-stone-400 text-sm">
                      <Calendar className="w-3.5 h-3.5" />
                      {formatDate(posts[0].published_at)}
                      {posts[0].author && posts[0].author !== 'SermonSift' && (
                        <span className="text-stone-300">· {posts[0].author}</span>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {/* Remaining posts grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {(page === 1 && !tag ? posts.slice(1) : posts).map(post => (
                <Link key={post.post_id} to={`/blog/${post.slug}`} className="group card p-0 overflow-hidden flex flex-col hover:shadow-lg transition-shadow">
                  {post.featured_image ? (
                    <div className="h-48 overflow-hidden flex-shrink-0">
                      <img
                        src={post.featured_image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="h-48 bg-gradient-to-br from-[#1e3a8a]/80 to-[#2d52b0]/80 flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-10 h-10 text-white/30" />
                    </div>
                  )}
                  <div className="p-5 flex flex-col flex-1">
                    {post.tags?.length > 0 && (
                      <button
                        onClick={e => { e.preventDefault(); setSearchParams({ tag: post.tags[0] }); }}
                        className="inline-flex items-center gap-1 text-[#1e3a8a] text-xs font-semibold uppercase tracking-wider mb-2 hover:underline w-fit"
                      >
                        <Tag className="w-3 h-3" /> {post.tags[0]}
                      </button>
                    )}
                    <h3 className="font-serif text-lg font-bold text-[#1e3a8a] mb-2 leading-snug group-hover:text-[#2d52b0] transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    {post.excerpt && (
                      <p className="text-stone-500 text-sm leading-relaxed line-clamp-3 flex-1 mb-4">{post.excerpt}</p>
                    )}
                    <div className="flex items-center gap-1.5 text-stone-400 text-xs mt-auto">
                      <Calendar className="w-3 h-3" />
                      {formatDate(post.published_at)}
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-12">
                <Button
                  variant="ghost"
                  onClick={() => goPage(page - 1)}
                  disabled={page <= 1}
                  className="flex items-center gap-1.5"
                >
                  <ArrowLeft className="w-4 h-4" /> Previous
                </Button>
                <span className="text-sm text-stone-500">Page {page} of {pages}</span>
                <Button
                  variant="ghost"
                  onClick={() => goPage(page + 1)}
                  disabled={page >= pages}
                  className="flex items-center gap-1.5"
                >
                  Next <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="py-10 border-t border-stone-200 bg-[#fafaf9]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="font-serif text-lg font-bold text-[#1e3a8a]">SermonSift</span>
          <p className="text-sm text-stone-400">© {new Date().getFullYear()} Bethany Labs. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
